import { useEffect, useRef, useState, useCallback } from "react";

const isElementFullscreen = () =>
  !!(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );

export const requestProctoringFullscreen = (element = document.documentElement) => {
  const request =
    element.requestFullscreen ||
    element.webkitRequestFullscreen ||
    element.mozRequestFullScreen ||
    element.msRequestFullscreen;

  if (!request) return;

  try {
    const result = request.call(element);
    if (result && typeof result.catch === "function") {
      result.catch((err) => console.error("[Proctoring] requestFullscreen rejected:", err));
    }
  } catch (e) {
    console.error("[Proctoring] requestFullscreen threw:", e);
  }
};

const exitProctoringFullscreen = () => {
  if (!isElementFullscreen()) return;

  const exit =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.mozCancelFullScreen ||
    document.msExitFullscreen;

  if (!exit) return;

  try {
    const result = exit.call(document);
    if (result && typeof result.catch === "function") {
      result.catch(() => {});
    }
  } catch (e) {
    // ignore
  }
};

// Blocks the keyboard shortcuts commonly used to open browser DevTools or view/save page source.
const isBlockedShortcut = (e) => {
  const key = e.key?.toLowerCase();
  return (
    e.key === "F12" ||
    (e.ctrlKey && e.shiftKey && ["i", "j", "c"].includes(key)) ||
    (e.metaKey && e.altKey && ["i", "j", "c"].includes(key)) ||
    (e.ctrlKey && ["u", "s", "p"].includes(key))
  );
};

const useProctoring = ({ enabled, onViolation, blockCopyPaste = true }) => {
  const [violationCounts, setViolationCounts] = useState({
    fullscreenExit: 0,
    tabSwitch: 0,
    blockedShortcut: 0,
    copyAttempt: 0,
  });
  const [isContainerFullscreen, setIsContainerFullscreen] = useState(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const reportViolation = useCallback(
    (type) => {
      setViolationCounts((prev) => ({ ...prev, [type]: (prev[type] || 0) + 1 }));
      if (onViolation) onViolation(type);
    },
    [onViolation]
  );

  useEffect(() => {
    if (!enabled) return;

    const handleFullscreenChange = () => {
      const fullscreenActive = isElementFullscreen();
      setIsContainerFullscreen(fullscreenActive);
      if (enabledRef.current && !fullscreenActive) {
        reportViolation("fullscreenExit");
      }
    };

    const handleVisibilityChange = () => {
      if (enabledRef.current && document.hidden) {
        reportViolation("tabSwitch");
      }
    };

    const handleWindowBlur = () => {
      if (enabledRef.current && !document.hidden) {
        reportViolation("tabSwitch");
      }
    };

    const blockEvent = (e) => {
      if (enabledRef.current) e.preventDefault();
    };

    const blockAndFlagCopyEvent = (e) => {
      if (enabledRef.current) {
        if (blockCopyPaste) e.preventDefault();
        reportViolation("copyAttempt");
      }
    };

    const handleKeyDown = (e) => {
      if (enabledRef.current && isBlockedShortcut(e)) {
        e.preventDefault();
        e.stopPropagation();
        reportViolation("blockedShortcut");
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("blur", handleWindowBlur);

    document.addEventListener("contextmenu", blockEvent);
    document.addEventListener("copy", blockAndFlagCopyEvent);
    document.addEventListener("cut", blockAndFlagCopyEvent);
    document.addEventListener("paste", blockAndFlagCopyEvent);

    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);

      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("blur", handleWindowBlur);

      document.removeEventListener("contextmenu", blockEvent);
      document.removeEventListener("copy", blockAndFlagCopyEvent);
      document.removeEventListener("cut", blockAndFlagCopyEvent);
      document.removeEventListener("paste", blockAndFlagCopyEvent);

      document.removeEventListener("keydown", handleKeyDown, true);

      setIsContainerFullscreen(false);
      exitProctoringFullscreen();
    };
  }, [enabled, reportViolation]);

  const proctoringLogs = [
    { type: "fullscreenExit",  label: "Fullscreen Exit",           count: violationCounts.fullscreenExit  },
    { type: "tabSwitch",       label: "Tab / Window Switch",       count: violationCounts.tabSwitch       },
    { type: "blockedShortcut", label: "DevTools / Shortcut Block", count: violationCounts.blockedShortcut },
    { type: "copyAttempt",     label: "Copy / Paste Attempt",      count: violationCounts.copyAttempt    },
  ];

  return {
    violationCounts,
    proctoringLogs,
    requestFullscreen: requestProctoringFullscreen,
    isContainerFullscreen,
  };
};

export default useProctoring;
